import { t } from 'i18next'
import { z } from 'zod'

import { AreaCode } from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import {
  DeliveryHourType,
  DeliveryPeriodType,
  DeliveryType,
} from '@enechain/eposition-proto/types/v1/deal_pb'
import { validateUtils } from '@enechain/eposition-utils'

type TransactionSchemaOptions = {
  allowFractionalVolume?: boolean
}

// 数量(MW)のスキーマ。許容単位は取引種別で変わるため、単体でも組み立てられるようにしている。
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createVolumeInKwSchema(allowFractionalVolume = false) {
  return (
    z
      .string()
      // 上限は backend の maxVolumeInKW = 9_999_000 kW (= 9999 MW) に合わせる。
      // 「< 10000」だと小数許可時に 9999.9 がクライアントを通過してサーバーで拒否される。
      .refine((value) => Number(value) > 0 && Number(value) <= 9999, {
        params: {
          i18n: {
            key: allowFractionalVolume
              ? 'validation:errors.custom.invalid_volume_in_kw_fractional'
              : 'validation:errors.custom.invalid_volume_in_kw',
          },
        },
      })
      .refine(
        (value) =>
          allowFractionalVolume
            ? validateUtils.isToSpecifiedDecimalPlace(value, 1)
            : Number.isInteger(Number(value)),
        {
          params: {
            i18n: {
              key: allowFractionalVolume
                ? 'validation:errors.custom.invalid_volume_in_kw_fractional'
                : 'validation:errors.custom.invalid_volume_in_kw',
            },
          },
        },
      )
  )
}

type VolumeInKwCarrier = {
  volumeInKw: string
}

export type PostingVolumeValue = {
  isPhysicalFuture: boolean
  isSplitInput: boolean
  spreadType: {
    isSpreadTrade: boolean
  }
  baseSummaryOrder: VolumeInKwCarrier
  exchangeSummaryOrder?: VolumeInKwCarrier
  baseTransactions?: VolumeInKwCarrier[]
  exchangeTransactions?: VolumeInKwCarrier[]
}

// ポスティング入力の数量を検証する。現先は 1MW 単位、非現先は 0.1MW 単位。
// 許容単位が値(isPhysicalFuture)で決まるためスキーマ構築時には固定できず、superRefine で検証する。
// zod は transform より後ろの refinement を入力が dirty なとき実行しないので、
// 呼び出し側では transform の手前に置くこと。さもないと他項目が未入力の間だけ数量のエラーが出なくなる。
export function refinePostingVolumeInKw(
  value: PostingVolumeValue,
  ctx: z.RefinementCtx,
): void {
  const volumeInKwSchema = createVolumeInKwSchema(!value.isPhysicalFuture)

  const targets: Array<{
    volumeInKw: string
    path: Array<string | number>
  }> = [
    {
      volumeInKw: value.baseSummaryOrder.volumeInKw,
      path: ['baseSummaryOrder'],
    },
  ]
  if (
    value.spreadType.isSpreadTrade &&
    value.exchangeSummaryOrder !== undefined
  ) {
    targets.push({
      volumeInKw: value.exchangeSummaryOrder.volumeInKw,
      path: ['exchangeSummaryOrder'],
    })
  }
  if (value.isSplitInput) {
    for (const [index, transaction] of (
      value.baseTransactions ?? []
    ).entries()) {
      targets.push({
        volumeInKw: transaction.volumeInKw,
        path: ['baseTransactions', index],
      })
    }
    if (value.spreadType.isSpreadTrade) {
      for (const [index, transaction] of (
        value.exchangeTransactions ?? []
      ).entries()) {
        targets.push({
          volumeInKw: transaction.volumeInKw,
          path: ['exchangeTransactions', index],
        })
      }
    }
  }

  for (const target of targets) {
    const result = volumeInKwSchema.safeParse(target.volumeInKw)
    if (result.success) {
      continue
    }
    for (const issue of result.error.issues) {
      ctx.addIssue({ ...issue, path: [...target.path, 'volumeInKw'] })
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createTransactionSchema({
  allowFractionalVolume = false,
}: TransactionSchemaOptions = {}) {
  return z.object({
    areaCode: z
      .nativeEnum(AreaCode)
      .refine((value) => value !== AreaCode.UNSPECIFIED, {
        params: {
          i18n: 'validation:errors.custom.required_select',
        },
      }),
    deliveryPeriodType: z.nativeEnum(DeliveryPeriodType),
    deliveryDateRange: z.object({
      deliveryStartDate: z.date({
        errorMap: () => ({
          message: t('validation:errors.custom.required_delivery_start_date'),
        }),
      }),
      deliveryEndDate: z.date({
        errorMap: () => ({
          message: t('validation:errors.custom.required_delivery_end_date'),
        }),
      }),
    }),
    deliveryHourType: z
      .nativeEnum(DeliveryHourType)
      .refine((value) => value !== DeliveryHourType.UNSPECIFIED, {
        params: {
          i18n: 'validation:errors.custom.required_select',
        },
      }),
    eclearDeliveryType: z.nativeEnum(DeliveryType).optional(),
    eexInstrument: z.string().optional(),
    unitPricePerKwh: z
      .string()
      .refine((value) => Number(value) > 0 && Number(value) < 1000, {
        params: {
          i18n: {
            key: 'validation:errors.custom.invalid_unit_price_per_kwh',
          },
        },
      })
      .refine((value) => validateUtils.isToSpecifiedDecimalPlace(value, 2), {
        params: {
          i18n: {
            key: 'validation:errors.invalid_string.regex',
          },
        },
      }),
    volumeInKw: createVolumeInKwSchema(allowFractionalVolume),
    floatingIndexCode: z
      .string()
      .nullable()
      .optional()
      .transform((v) => v ?? undefined),
  })
}

// 後方互換: 既定（1MW単位）のスキーマ
export const transactionSchema = createTransactionSchema()
export type TransactionSchema = z.input<
  ReturnType<typeof createTransactionSchema>
>
